import { Menu, Search as SearchIcon, X } from 'lucide-react';
import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { getRepo, type Category, type Settings } from '../lib/repo';
import { useAsync } from '../lib/hooks';
import { Button } from '../components/ui';
import { SearchDialog } from '../components/SearchDialog';

function Logo({ onClick }: { onClick?: () => void }) {
  return (
    <Link to="/" onClick={onClick} className="font-display text-2xl font-semibold tracking-tight">
      The <span className="italic text-primary">Story</span> Line
    </Link>
  );
}

const STATIC_LINKS = [
  { to: '/latest', label: 'Latest' },
  { to: '/trending', label: 'Trending' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

export function PublicLayout() {
  const { data: categories } = useAsync<Category[]>(() => getRepo().listCategories(), []);
  const { data: settings } = useAsync<Settings>(() => getRepo().getSettings(), []);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  const socials = settings?.social ?? { facebook: '', twitter: '', instagram: '', youtube: '' };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4">
          <Button variant="ghost" size="sm" className="md:hidden" aria-label="Menu" onClick={() => setMenuOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
          <div className="text-center md:text-left">
            <Logo />
            <p className="hidden text-[11px] uppercase tracking-[0.2em] text-muted md:block">Stories, art and culture, told well</p>
          </div>
          <Button variant="ghost" size="sm" aria-label="Search" onClick={() => setSearchOpen(true)}>
            <SearchIcon className="h-5 w-5" />
          </Button>
        </div>
        <nav aria-label="Sections" className="hidden border-t border-border md:block">
          <ul className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-6 gap-y-2 px-4 py-2.5 text-[12px] font-medium uppercase tracking-wider">
            {(categories ?? []).map((c) => (
              <li key={c.id}>
                <NavLink to={`/category/${c.slug}`} className={({ isActive }) => (isActive ? 'text-primary' : 'text-foreground/80 hover:text-primary')}>
                  {c.name}
                </NavLink>
              </li>
            ))}
            <li className="text-border">|</li>
            {STATIC_LINKS.map((l) => (
              <li key={l.to}>
                <NavLink to={l.to} className={({ isActive }) => (isActive ? 'text-primary' : 'text-foreground/80 hover:text-primary')}>{l.label}</NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      {menuOpen ? (
        <div className="fixed inset-0 z-50 bg-background md:hidden" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="flex items-center justify-between border-b border-border px-4 py-4">
            <Logo onClick={() => setMenuOpen(false)} />
            <Button variant="ghost" size="sm" aria-label="Close menu" onClick={() => setMenuOpen(false)}><X className="h-5 w-5" /></Button>
          </div>
          <nav className="p-6">
            <ul className="space-y-4 text-2xl font-display">
              {(categories ?? []).map((c) => (
                <li key={c.id}><Link to={`/category/${c.slug}`} onClick={() => setMenuOpen(false)}>{c.name}</Link></li>
              ))}
            </ul>
            <ul className="mt-8 space-y-3 border-t border-border pt-6 text-sm">
              {STATIC_LINKS.map((l) => <li key={l.to}><Link to={l.to} onClick={() => setMenuOpen(false)}>{l.label}</Link></li>)}
            </ul>
          </nav>
        </div>
      ) : null}

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="mt-16 bg-accent text-white/90">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 md:grid-cols-4">
          <div className="md:col-span-1">
            <p className="font-display text-xl text-white">{settings?.title ?? 'The Story Line'}</p>
            <p className="mt-2 text-sm text-white/70">{settings?.description}</p>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-white/60">Sections</h3>
            <ul className="mt-3 space-y-2 text-sm">
              {(categories ?? []).slice(0, 4).map((c) => <li key={c.id}><Link className="hover:text-white" to={`/category/${c.slug}`}>{c.name}</Link></li>)}
            </ul>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-white/60">More</h3>
            <ul className="mt-3 space-y-2 text-sm">
              {(categories ?? []).slice(4).map((c) => <li key={c.id}><Link className="hover:text-white" to={`/category/${c.slug}`}>{c.name}</Link></li>)}
              <li><Link className="hover:text-white" to="/latest">Latest</Link></li>
              <li><Link className="hover:text-white" to="/trending">Trending</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-white/60">About</h3>
            <ul className="mt-3 space-y-2 text-sm">
              <li><Link className="hover:text-white" to="/about">About</Link></li>
              <li><Link className="hover:text-white" to="/contact">Contact</Link></li>
              <li><Link className="hover:text-white" to="/advertise">Advertise</Link></li>
              <li><Link className="hover:text-white" to="/terms">Terms</Link></li>
              <li><Link className="hover:text-white" to="/privacy">Privacy</Link></li>
            </ul>
            {Object.values(socials).some(Boolean) ? (
              <ul className="mt-4 flex gap-3 text-sm">
                {socials.facebook ? <li><a href={socials.facebook} rel="me noopener" target="_blank">Facebook</a></li> : null}
                {socials.twitter ? <li><a href={socials.twitter} rel="me noopener" target="_blank">X</a></li> : null}
                {socials.instagram ? <li><a href={socials.instagram} rel="me noopener" target="_blank">Instagram</a></li> : null}
                {socials.youtube ? <li><a href={socials.youtube} rel="me noopener" target="_blank">YouTube</a></li> : null}
              </ul>
            ) : null}
          </div>
        </div>
        <div className="border-t border-white/10 px-4 py-4 text-center text-xs text-white/60">
          © {new Date().getFullYear()} {settings?.title ?? 'The Story Line'}. All rights reserved.
        </div>
      </footer>

      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
      {/* keyed by pathname so a navigation always closes any open overlay */}
      <span className="hidden" data-route={location.pathname} />
    </div>
  );
}
